<template>
  <header class="ln-hero" ref="hero" id="signup">
    <div class="ln-hero__cover" :class="'ln-hero__cover--' + classNameCover">
      <router-link to="#" class="ln-hero__cover-logo">
        <mq-layout :mq="['m480', 'm576', 'm768']">
          <img v-show="logoStyle == 'dark'" src="../../../assets/img/logos/logo-hellowish-dark.webp" alt="" />
          <img v-show="logoStyle == 'white'" src="../../../assets/img/logos/logo-hellowish-white.webp" alt="" />
        </mq-layout>
        <mq-layout mq="m1200">
          <img src="../../../assets/img/logos/logo-hellowish-dark.webp" alt="" />
        </mq-layout>
      </router-link>
      <picture class="ln-hero__inset" :class="{'ln-hero__inset--vertical': coverIsVertical}">
        <slot name="cover"></slot>
      </picture>
    </div>

    <aside class="ln-hero__col">
      <section class="ln-hero-form">
        <div class="ln-hero-form__logo">
          <img src="../../../assets/img/logos/logo-hellowish-dark.webp" alt="" />
        </div>
        <h1 class="ln-hero-form__title" ref="scrolltoResponsive">{{ title }}</h1>
        <h2 class="ln-hero-form__subtitle" v-if="subtitle">{{ subtitle }}</h2>

        <!-- Tu formulario actual (sin cambios de lógica) -->
        <div v-show="true">
          <div class="ln-hero-form__form">
            <InputText
              :label="$t('landings.hero.yourmail', lang)"
              v-model="email"
              :errorLabel="emailError"
              :placeholder="$t('landings.hero.yourmailplaceholder', lang)"
              ref="heroEmail"
              @focus="emailError = ''"
              @keyup.native="showPassword = true"
            />
            <transition name="slide-fade" mode="out-in" appear>
              <div v-if="showPassword" aria-live="polite">
                <InputText
                  ref="passwordInput"
                  :label="$t('landings.hero.yourpassword', lang)"
                  v-model="password"
                  type="password"
                  :errorLabel="passwordError"
                  :placeholder="$t('landings.hero.yourpasswordplaceholder', lang)"
                  @focus="passwordError = ''"
                />
              </div>
            </transition>

            <div class="ln-hero-form__aceptance">
              <label class="w-checkbox checkbox-field">
                <label style="display: inline;">
                  <span v-html="$t('signup.acceptTCpart1ImplicitVersion', lang)"></span>
                  <router-link
                    :to="$t('routes.confidentialityPolicy', lang)"
                    style="text-decoration: underline;"
                    target="_blank"
                  >{{ $t("signup.acceptTCpart2", lang) }}</router-link>.
                </label>
              </label>
            </div>
          </div>

          <div class="ln-hero-form__actions">
            <bbButton
              class="button--accent button--block"
              :label="buttonText"
              @click="signUp"
              :loading="loading"
            />
          </div>

          <login-social
            :show="true"
            :showFacebook="false"
            :showLabel="true"
            class="login-box__social"
            color="yellow"
            size="large"
            :origin="origin"
            :forcedLang="lang"
            :listType="listType"
            :verticalAlign="true"
            :promoCode="promoCode"
          />
        </div>
      </section>
    </aside>

    <!-- Botón sticky: solo visible cuando el hero no está en viewport -->
    <button
      class="cta-sticky"
      v-show="showStickyCta"
      @click="scrollToHero"
      aria-label="Crear tu lista"
    >
      {{ buttonText }}
    </button>
  </header>
</template>

<script>
import LoginSocial from "../../../components/ui/login/LoginSocial.vue";
import InputText from "../../../components/forms/InputText.vue";
import bbButton from "../../../components/buttons/bbButton.vue";
import axios from "axios";
const crypto = require("crypto");
import loginMixin from "../../mixins/login.js";

export default {
  name: "LandingHero",
  mixins: [loginMixin],
  components: { LoginSocial, InputText, bbButton },
  props: {
    title: { type: String, required: true },
    buttonText: { type: String, default: "Empieza tu lista ahora" },
    subtitle: { type: String, required: false },
    origin: { type: String, required: true },
    showLoginSocial: { type: Boolean, default: false },
    classNameCover: String,
    coverIsVertical: { type: Boolean, default: false },
    lang: { type: String, default: "es-ES" },
    listType: { type: String, default: "baby-registry" },
    logoStyle: { type: String, default: "dark" },
    promoCode: { type: String, default: "" }
  },
  data() {
    return {
      username: "",
      email: "",
      password: "",
      privacity: false,
      loading: false,
      emailError: "",
      usernameError: "",
      passwordError: "",
      privacityError: "",
      showPassword: false,
      showStickyCta: false,      // << añadido
      _io: null                  // << añadido: referencia IntersectionObserver
    };
  },
  computed: {
    isFormValid() {
      return this.email && this.password && this.validateEmail();
    }
  },
  mounted() {
    // Mostrar CTA sticky solo cuando el hero deja de ser visible
    const heroEl = this.$refs.hero;
    if (!heroEl) return;

    this._io = new IntersectionObserver(
      ([entry]) => {
        this.showStickyCta = !entry.isIntersecting;
      },
      { threshold: 0.12 } // margen razonable para no parpadear
    );
    this._io.observe(heroEl);
  },
  beforeDestroy() {
    if (this._io) {
      try { this._io.disconnect(); } catch(e) {}
      this._io = null;
    }
  },
  methods: {
    async goToSignupSecondStep() {
      this.email = this.email.trim();
      if (!this.validateEmail()) {
        this.emailError = this.$t("landings.hero.emailisnotvalid", this.lang);
        return;
      }
      this.loading = true;
      this.resetErrors();
      this.$router.push({
        name: "signup-landing",
        query: {
          email: this.email,
          backUrl: this.$route.path,
          origin: this.origin,
          listType: this.listType
        }
      });
    },

    async signUp() {
      this.email = this.email.trim();
      this.loading = true;
      this.resetErrors();

      if (!this.email || !this.password || !this.privacity) {
        if (!this.email) this.emailError = this.$t("landings.hero.emailisneeded", this.lang);
        if (!this.password) this.passwordError = this.$t("landings.hero.passwordisneeded", this.lang);
      }

      if (!this.validateEmail()) {
        this.email = this.email.trim();
        this.emailError = this.$t("landings.hero.emailisnotvalid", this.lang);
        this.loading = false;
      }

      if (!this.isFormValid) return;

      let source_url = "";
      try { source_url = localStorage.getItem("source_url"); } catch (e) {}

      const baseURL = process.env.API;
      try {
        await axios.post(`${baseURL}/user/signup`, {
          name: this.username,
          email: this.email,
          password: this.password,
          SignupOrigin: this.origin,
          listType: this.listType,
          referralUrl: source_url,
          language: this.lang,
          promoCode: this.promoCode
        });
        this.setModalShownCookie();
      } catch (error) {
        if (parseInt(error.response?.status) === 409) {
          this.loading = false;
          this.error = "Este email ya ha sido registrado. ¿Quieres iniciar sesión?";
          await this.$sleep(this.goToLogin, 2000);
          return;
        }
      }

      window.mixpanel?.people?.set?.("Signup Origin", this.origin);
      this.$root?.trackConversion?.({ email: this.email });

      if (this.listType == "wishlist") return this.login(`/onboarding-0?newuser=true`);
      else return this.login(`/onboarding/${encodeURIComponent(this.listType)}`);
    },

    validateEmail() {
      const email = (this.email || "").toLowerCase();
      const parts = email.split("@");
      if (parts.length !== 2) return false;
      const [left, right] = parts;
      return left.match(/^\w+([.-_+]\w+)*$/) && right.match(/^\w+([.-]\w+)*\.\w{2,10}$/);
    },

    setModalShownCookie() {
      const days = 30;
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + days);
      const currentTimestamp = new Date().getTime().toString();
      const randomValue = Math.random().toString();
      const uniqueHash = require("crypto").createHash("sha256").update(currentTimestamp + randomValue).digest("hex");
      document.cookie = `modalShown=${uniqueHash}; expires=${expirationDate.toUTCString()}; path=/`;
    },

    async goToLogin() {
      await this.$router.push({ name: "login" });
    },

    resetErrors() {
      this.usernameError = "";
      this.emailError = "";
      this.passwordError = "";
      this.privacityError = "";
    },

    scrollToHero() {
      //if mobile screen, scroll to scrollto - 12. If not, go to the top of the page
      if (window.innerWidth < 768) {
        const heroFormEl = this.$refs.scrolltoResponsive;
        const offset = 12;
        if (heroFormEl?.scrollIntoView) {
          const elementPosition = heroFormEl.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - offset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      // const scrollEl = this.$refs.scrollto;
      //   if (scrollEl?.scrollIntoView) {
      //     scrollEl.scrollIntoView({ behavior: "smooth", block: "start" });
      //   } else {
      //     window.scrollTo({ top: 0, behavior: "smooth" });
      //   }

      //   setTimeout(() => {
      //     const emailEl =
      //       this.$refs.heroEmail?.$el?.querySelector?.("input") ||
      //       this.$refs.heroEmail?.$refs?.input;
      //     try { emailEl?.focus(); } catch (e) {}
      //   }, 400);
    }
  }
};
</script>

<style scoped>
/* --- Transición que ya tenías --- */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: max-height 0.4s ease, opacity 0.4s ease;
  overflow: hidden;
}
.slide-fade-enter, 
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-fade-enter-to, 
.slide-fade-leave {
  max-height: 200px;
  opacity: 1;
}

/* --- Botón sticky --- */
.cta-sticky {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1000;
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  background: #5d50c6;
  color: #fff;           /* contraste alto */
  box-shadow: 0 8px 24px rgba(0,0,0,.18);
  transition: transform .15s ease, opacity .25s ease;
}
.cta-sticky:active { transform: translateY(1px); }

/* Desktop: puedes moverlo arriba si prefieres */
@media (min-width: 992px) {
  .cta-sticky { bottom: 24px; right: 24px; }
}

/* Evitar que tape elementos interactivos bajos (barra cookies, etc.) */
@media (max-width: 768px) {
  .cta-sticky { bottom: 20px; right: 12px; }
}

/* --- Tus estilos existentes --- */
.landing-vertical-cover { }
.landing-header { background-color: #ffc944; }
.landing-logo { display:none; }
@media screen and (max-width: 768px) {
  .landing-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background-color: #ffc944;
  }
  .landing-logo { display: flex; width: 100%; padding-left: 1rem; padding-top: 1rem; }
  .landing-logo img { width: 120px; }
}
</style>
